import React, { useState, useRef, useEffect, useCallback, lazy } from 'react';
import '../styles/training.css';
import {
    initCamera,
    validateRecordingSettings,
    handleRecordingProcess,
    fetchGestureCounts,
    fetchUserSettings,
    saveUserSettings,
    clearVideoMetadataCache
} from '../utils/TrainingHelpers';
import VideoList from '../components/Training/VideoList';
import RecordingControls from '../components/Training/RecordingControls';
import SetProsodyRecordingSettings from '../components/Training/SetProsodyRecordingSettings';
import VideoPlayback from '../components/Training/VideoPlayback';
import SampleGesturePopup from '../components/Training/SampleGesturePopup';
import SettingsPanel from '../components/Training/SettingsPanel';
import { gestureSets, formatGestureForDisplay, gestureInstructions } from '../utils/ProsodyGestureSets';
import { FaQuestion } from 'react-icons/fa';

const RecordingStatusIndicator = lazy(() => import('../components/Training/RecordingStatusIndicator'));

const DEFAULT_PROSODY_COUNTDOWN = 2;
const BATCH_SIZE = 22;

const ProsodyTraining = () => {
    const [gesture, setGesture] = useState('');
    const [selectedSet, setSelectedSet] = useState('');
    const [videos, setVideos] = useState([]);
    const [error, setError] = useState('');
    const [showCamera, setShowCamera] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [stream, setStream] = useState(null);
    const [selectedVideos, setSelectedVideos] = useState(new Set());
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState(new Set());
    const [tempFilters, setTempFilters] = useState(new Set());
    const [currentCount, setCurrentCount] = useState(0);
    const [countdown, setCountdown] = useState(0);
    const currentBatchIdRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [gestureError, setGestureError] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isBatchActive, setIsBatchActive] = useState(false);
    const [sampleVideoUrl, setSampleVideoUrl] = useState(null);
    const [showSamplePopup, setShowSamplePopup] = useState(false);
    const [showHelpHint, setShowHelpHint] = useState(false);
    const helpHintTimerRef = useRef(null);
    const videoRef = useRef(null);
    const currentBatchRecordingsRef = useRef([]);
    const videoListRef = useRef(null);
    const [prosodyCountdownLength, setProsodyCountdownLength] = useState(DEFAULT_PROSODY_COUNTDOWN);
    const [showSettings, setShowSettings] = useState(false);
    const [gestureCounts, setGestureCounts] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [currentInstructions, setCurrentInstructions] = useState([]);
    const [replacementCount, setReplacementCount] = useState(null);
    const [preAssignedBatchId, setPreAssignedBatchId] = useState(null);
    const [lockedBatchId, setLockedBatchId] = useState(null);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await fetchUserSettings();
                if (settings.prosodyCountdown) {
                    setProsodyCountdownLength(settings.prosodyCountdown);
                }
            } catch (err) {
                console.error('Error fetching user settings:', err);
            }
        };
        loadSettings();
    }, []);

    useEffect(() => {
        if (showCamera) {
            initCamera(videoRef, setStream, setError);
        }
    }, [showCamera]);

    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(t => t.stop());
            }
        };
    }, [stream]);

    useEffect(() => {
        clearVideoMetadataCache();
    }, []);

    const countdownTimerRef = useRef(null);
    const startCountdown = useCallback((length) => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
        setCountdown(length);
        return new Promise(resolve => {
            countdownTimerRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                        resolve();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);

    // Cleanup help hint timer on unmount
    useEffect(() => {
        return () => {
            if (helpHintTimerRef.current) {
                clearTimeout(helpHintTimerRef.current);
            }
        };
    }, []);

    const updateGestureCounts = useCallback((gestureName, increment, newCounts = null, newTotal = null) => {
        if (newCounts !== null && newTotal !== null) {
            setGestureCounts(newCounts);
            setTotalCount(newTotal);
        } else if (gestureName) {
            setGestureCounts(prev => ({ ...prev, [gestureName]: (prev[gestureName] || 0) + increment }));
            setTotalCount(prev => prev + increment);
        }
    }, []);

    useEffect(() => {
        const loadCounts = async () => {
            try {
                const data = await fetchGestureCounts(false, null, 'prosody');
                updateGestureCounts(null, 0, data.counts, data.total);
            } catch (err) {
                console.error('Error fetching gesture counts:', err);
            }
        };
        loadCounts();
    }, [updateGestureCounts]);

    useEffect(() => {
        if (!isBatchActive &&
            replacementCount === null &&
            gesture &&
            gestureCounts[gesture] > 0) {
            setGesture('');
        }
    }, [gestureCounts, gesture, replacementCount, isBatchActive]);

    const resetReplacementMode = useCallback(() => {
        setReplacementCount(null);
        setPreAssignedBatchId(null);
        setLockedBatchId(null);
    }, []);

    const cancelReplacementMode = useCallback(() => {
        setReplacementCount(null);
        setPreAssignedBatchId(null);
        setLockedBatchId(null);
        setSelectedSet('');
        setGesture('');
    }, []);

    const beginReplacement = useCallback((batchId, gestureName, missingCount) => {
        const setObj = gestureSets.find(s => s.gestures.includes(gestureName));
        if (setObj) setSelectedSet(setObj.id);
        setGesture(gestureName);
        setGestureError(false);
        setSampleVideoUrl(null);
        setShowSamplePopup(false);
        setReplacementCount(missingCount);
        setPreAssignedBatchId(batchId);
        setLockedBatchId(batchId);
    }, []);

    const startNextRecording = useCallback(async (count, stepTotal, orientation, onComplete) => {
        await handleRecordingProcess(
            count,
            stepTotal,
            currentBatchIdRef.current,
            stream,
            () => startCountdown(prosodyCountdownLength),
            setIsRecording,
            setCurrentCount,
            setIsUploading,
            setError,
            setVideos,
            gesture,
            false,
            currentBatchRecordingsRef,
            false,
            updateGestureCounts,
            orientation,
            false,
            null,
            'prosody',
            onComplete
        );
    }, [stream, gesture, startCountdown, prosodyCountdownLength, updateGestureCounts]);

    const startRecording = useCallback(async () => {
        const hasError = validateRecordingSettings(
            gesture,
            stream,
            setError,
            setGestureError
        );
        if (hasError) return;

        setGestureError(false);
        setError('');
        setIsRecording(true);
        const batchId = preAssignedBatchId || `${Date.now()}`;
        currentBatchIdRef.current = batchId;
        if (preAssignedBatchId) setPreAssignedBatchId(null);
        setLockedBatchId(null);
        currentBatchRecordingsRef.current = [];
        setIsBatchActive(true);
        setCurrentInstructions(gestureInstructions[gesture] || []);
        setCurrentCount(0);
        const total = replacementCount !== null ? replacementCount : BATCH_SIZE;
        await startNextRecording(0, total, 'front', () => {
            setIsRecording(false);
            setIsBatchActive(false);
            setCurrentInstructions([]);
            if (videoListRef.current) {
                if (videoListRef.current.refreshVisibleBatches) {
                    videoListRef.current.refreshVisibleBatches();
                }
                if (videoListRef.current.refreshBatch && currentBatchIdRef.current) {
                    videoListRef.current.refreshBatch(currentBatchIdRef.current);
                }
            }
            fetchGestureCounts(false, null, 'prosody')
                .then(data => updateGestureCounts(null, 0, data.counts, data.total))
                .catch(err => console.error('Error updating gesture counts:', err));
            resetReplacementMode();
        });
    }, [gesture, stream, startNextRecording, replacementCount, preAssignedBatchId, resetReplacementMode, updateGestureCounts]);

    const handleStartFromPopup = useCallback(() => {
        setShowSamplePopup(false);
        startRecording();
    }, [startRecording]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.code === 'Space' && showCamera) {
                e.preventDefault();
                if (showSamplePopup) {
                    handleStartFromPopup();
                } else if (!isRecording && !isUploading) {
                    setGestureError(false);
                    startRecording();
                }
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [showCamera, isRecording, isUploading, showSamplePopup, startRecording, handleStartFromPopup]);

    const handleGestureChange = async (e) => {
        if (!isRecording) {
            const value = e.target.value;
            setGesture(value);
            setGestureError(false);

            // For prosody training, sample videos live in a local directory
            // rather than being fetched from the training service.
            const localUrl = `/prosody_sample_videos/${encodeURIComponent(value)}.mp4`;
            setSampleVideoUrl(localUrl);
            setShowSamplePopup(true);
        }
    };

    const handleSetChange = (e) => {
        if (!isRecording) {
            setSelectedSet(e.target.value);
            setGesture('');
            setGestureError(false);
            setSampleVideoUrl(null);
            setShowSamplePopup(false);
        }
    };

    const showHintTemporarily = () => {
        setShowHelpHint(true);
        clearTimeout(helpHintTimerRef.current);
        helpHintTimerRef.current = setTimeout(() => setShowHelpHint(false), 3000);
    };

    const handleClosePopup = () => {
        setShowSamplePopup(false);
        showHintTemporarily();
    };

    const handleBackToCamera = () => {
        setSelectedVideo(null);
        setShowCamera(true);
    };

    const handleSaveSettings = async (settings) => {
        try {
            await saveUserSettings(settings);
        } catch (err) {
            console.error('Error saving settings:', err);
        }
    };

    return (
        <div className="training-container">
            <div className="video-section">
                {showCamera ? (
                    <>
                        <div className={`training-video-container ${isRecording && !isUploading && countdown === 0 ? 'recording-active' : ''}`}>
                            <video ref={videoRef} className="video-preview" autoPlay muted playsInline />
                            <RecordingStatusIndicator
                                isRecording={isRecording}
                                gesture={gesture}
                                currentCount={currentCount}
                                totalCount={replacementCount !== null ? replacementCount : BATCH_SIZE}
                                instructions={currentInstructions}
                                isUploading={isUploading}
                                isFirewallMode={false}
                                countdown={countdown}
                                isPaused={false}
                            />
                        </div>
                        <div className="recording-info">
                            {countdown > 0 && (
                                <div className="countdown">{countdown}</div>
                            )}
                        </div>
                        <RecordingControls
                            isRecording={isRecording}
                            startRecording={startRecording}
                            isUploading={isUploading}
                            isPaused={false}
                            onPauseResume={() => {}}
                            onAbort={() => window.location.reload()}
                            countdown={countdown}
                            onSettingsClick={() => setShowSettings(true)}
                            helpButton={
                                sampleVideoUrl && !showSamplePopup ? (
                                    <div className="help-button-container">
                                        <button
                                            className="sample-help-button"
                                            onClick={() => setShowSamplePopup(true)}
                                        >
                                            <FaQuestion style={{ width: '18px', height: '18px' }} />
                                        </button>
                                        {showHelpHint && <div className="help-hint" />}
                                    </div>
                                ) : null
                            }
                        />
                        {!isRecording && replacementCount === null && (
                            <SetProsodyRecordingSettings
                                gesture={gesture}
                                handleGestureChange={handleGestureChange}
                                selectedSet={selectedSet}
                                handleSetChange={handleSetChange}
                                isRecording={isRecording}
                                gestureError={gestureError}
                                sets={gestureSets}
                                gestureCounts={gestureCounts}
                            />
                        )}
                        {!isRecording && replacementCount !== null && (
                            <div className="replacement-banner">
                                <span>
                                    Recording {replacementCount} replacement(s) for {formatGestureForDisplay(gesture)}
                                </span>
                                <button className="back-button" onClick={cancelReplacementMode}>Back</button>
                            </div>
                        )}
                        {sampleVideoUrl && (
                            <SampleGesturePopup
                                visible={showSamplePopup}
                                gesture={formatGestureForDisplay(gesture)}
                                videoUrl={sampleVideoUrl}
                                instructions={gestureInstructions[gesture]}
                                onClose={handleClosePopup}
                                onStart={handleStartFromPopup}
                            />
                        )}
                        <SettingsPanel
                            visible={showSettings}
                            singleCountdown={true}
                            countdown={prosodyCountdownLength}
                            setCountdown={setProsodyCountdownLength}
                            onClose={() => setShowSettings(false)}
                            onSave={handleSaveSettings}
                        />
                    </>
                ) : (
                    <VideoPlayback
                        selectedVideo={selectedVideo}
                        handleBackToCamera={handleBackToCamera}
                    />
                )}
                {error && <p className="error-message">{error}</p>}
            </div>
            <VideoList
                videos={videos}
                setVideos={setVideos}
                setError={setError}
                selectedVideo={selectedVideo}
                setSelectedVideo={setSelectedVideo}
                setShowCamera={setShowCamera}
                selectedVideos={selectedVideos}
                setSelectedVideos={setSelectedVideos}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                showFilterPopup={showFilterPopup}
                setShowFilterPopup={setShowFilterPopup}
                tempFilters={tempFilters}
                setTempFilters={setTempFilters}
                gestures={gestureSets}
                isAdmin={false}
                handleRecordingProcess={startNextRecording}
                viewAllUsers={false}
                setViewAllUsers={() => {}}
                users={[]}
                selectedUser={''}
                setSelectedUser={() => {}}
                gestureCounts={gestureCounts}
                totalCount={totalCount}
                updateGestureCounts={updateGestureCounts}
                ref={videoListRef}
                isBatchActive={isBatchActive}
                lockedBatchId={lockedBatchId}
                isAdrian={false}
                onAddReplacements={beginReplacement}
                currentUserId={''}
                page="prosody"
            />
        </div>
    );
};

export default ProsodyTraining;
